import { ButtonHTMLAttributes, forwardRef, MouseEvent, ReactNode, useState } from 'react'
import { Link } from 'react-router-dom'
import { cn } from 'utils/cn'
import { handleLinkClick } from 'utils/linkClick'
import { Spinner } from './Spinner'

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'prefix'> {
    variant?: 'primary' | 'secondary' | 'tertiary' | 'outline' | 'ghost' | 'danger' | 'dangerSoft'
    size?: 'sm' | 'md' | 'lg'
    iconOnly?: boolean
    prefix?: ReactNode
    suffix?: ReactNode
    loading?: boolean
    /** Classes for the span that wraps `children`, e.g. `min-w-0 flex-1` so a long label truncates instead of pushing the suffix out. */
    contentClassName?: string
    /**
     * Opt-in: renders a real link (`<a href>`) instead of a `<button>`, so the browser's "Open link in new tab" works.
     * A plain left-click still runs `onClick` and navigates in-app; a disabled or loading button stays a `<button>`.
     */
    href?: string
    /** @internal used by ButtonGroup */
    hoverBg?: string
}

const sizeClasses: Record<string, { base: string; iconOnly: string; spinnerSize: number }> = {
    sm: { base: 'h-8 px-3 text-sm leading-5', iconOnly: 'h-8 p-2 text-sm leading-5', spinnerSize: 14 },
    md: { base: 'h-9 px-4 text-sm leading-5', iconOnly: 'h-9 p-2 text-sm leading-5', spinnerSize: 16 },
    lg: { base: 'h-10 px-5 text-base leading-6', iconOnly: 'h-10 p-2 text-base leading-6', spinnerSize: 18 },
}

const variantClasses: Record<string, string> = {
    primary: 'bg-accent text-accent-foreground enabled:hover:bg-accent-hover',
    secondary: 'bg-default text-default-foreground enabled:hover:bg-default-hover',
    tertiary: 'text-foreground enabled:hover:bg-default',
    outline: 'border border-border text-foreground enabled:hover:bg-default',
    ghost: 'text-foreground-muted enabled:hover:bg-default',
    danger: 'bg-danger text-danger-foreground enabled:hover:bg-danger-hover',
    dangerSoft: 'bg-danger-soft text-danger-soft-foreground enabled:hover:bg-danger-soft-hover',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            variant = 'primary',
            size = 'md',
            iconOnly = false,
            prefix,
            suffix,
            loading = false,
            disabled,
            children,
            className,
            contentClassName,
            style,
            hoverBg,
            href,
            onMouseEnter,
            onMouseLeave,
            ...props
        },
        ref
    ) => {
        const [hovered, setHovered] = useState(false)
        const sizeConf = sizeClasses[size]
        const isDisabled = disabled || loading

        const classes = cn(
            'inline-flex items-center justify-center gap-2 overflow-hidden whitespace-nowrap rounded-3xl font-medium transition-[background-color,opacity,box-shadow] duration-150 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
            !loading && 'disabled:opacity-50',
            iconOnly ? sizeConf.iconOnly : sizeConf.base,
            variantClasses[variant],
            className
        )
        const content = (
            <>
                {loading ? (
                    <Spinner size={sizeConf.spinnerSize} color="current" />
                ) : (
                    <>
                        {prefix && <span className="flex shrink-0 items-center">{prefix}</span>}
                        {children && <span className={cn('flex items-center', contentClassName)}>{children}</span>}
                        {suffix && <span className="flex shrink-0 items-center">{suffix}</span>}
                    </>
                )}
            </>
        )

        if (href && !isDisabled) {
            // The anchor keeps every data-* and aria-* attribute; `ref` is not forwarded (it is typed for the <button>).
            const linkAttrs = Object.fromEntries(Object.entries(props).filter(([key]) => key.startsWith('data-') || key.startsWith('aria-')))
            return (
                <Link
                    to={href}
                    {...linkAttrs}
                    id={props.id}
                    title={props.title}
                    className={cn(classes, 'no-underline')}
                    style={{ ...(hoverBg && hovered ? { background: hoverBg } : {}), ...style }}
                    onMouseEnter={(e) => {
                        setHovered(true)
                        onMouseEnter?.(e as unknown as MouseEvent<HTMLButtonElement>)
                    }}
                    onMouseLeave={(e) => {
                        setHovered(false)
                        onMouseLeave?.(e as unknown as MouseEvent<HTMLButtonElement>)
                    }}
                    onClick={(e) => handleLinkClick(e, props.onClick ? () => props.onClick?.(e as unknown as MouseEvent<HTMLButtonElement>) : undefined)}
                >
                    {content}
                </Link>
            )
        }

        return (
            <button
                ref={ref}
                disabled={isDisabled}
                onMouseEnter={(e) => {
                    setHovered(true)
                    onMouseEnter?.(e)
                }}
                onMouseLeave={(e) => {
                    setHovered(false)
                    onMouseLeave?.(e)
                }}
                className={classes}
                style={{
                    ...(hoverBg && hovered && !isDisabled ? { background: hoverBg } : {}),
                    ...style,
                }}
                {...props}
            >
                {content}
            </button>
        )
    }
)

Button.displayName = 'Button'
